import { useMemo } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { useQuery } from "@tanstack/react-query";

import CustomerPortalLayout from "components/storefront/CustomerPortalLayout";
import {
  PortalAlert,
  PortalButton,
  PortalCard,
  PortalLoading,
  PortalStatusChip,
} from "components/portal/PortalUi";
import MDTypography from "components/MDTypography";
import { useAuth } from "context/AuthContext";
import { get } from "lib/api";
import { isDistributorRole, isPortalRole } from "lib/portalRoles";
import useStorefrontCopy from "hooks/useStorefrontCopy";

function prettyStatus(value) {
  return String(value || "not_started")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

export default function CustomerValveBodies() {
  const { token, pluginRole, authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useStorefrontCopy();
  const role = String(pluginRole || "").toLowerCase();
  const isPortal = isPortalRole(role);
  const distributor = isDistributorRole(role);

  const query = useQuery({
    queryKey: ["storefront-valve-bodies", role],
    queryFn: () => get("/api/storefront/valve-bodies/me"),
    enabled: !!token && isPortal,
    staleTime: 15000,
  });
  const meQuery = useQuery({
    queryKey: ["storefront-me", "capabilities"],
    queryFn: () => get("/api/storefront/me"),
    enabled: !!token && isPortal,
    staleTime: 15000,
  });

  const rows = useMemo(
    () => (Array.isArray(query.data?.data) ? query.data.data : []),
    [query.data]
  );
  const capabilities = meQuery.data?.capabilities || {};
  const canSubmitCore = capabilities.submitCoreReturn !== false;
  const canSubmitWarranty = capabilities.submitWarranty !== false;

  if (!token)
    return (
      <Navigate
        to="/authentication/sign-in"
        state={{ from: location }}
        replace
      />
    );
  if (authLoading || pluginRole == null) return null;
  if (!isPortal) return <Navigate to="/dashboard" replace />;

  return (
    <CustomerPortalLayout
      title={t("registeredUnits.title", "Registered Items")}
      subtitle={
        distributor
          ? t(
              "registeredUnits.distributorSubtitle",
              "Track sold distributor units and end-user registration status."
            )
          : t(
              "registeredUnits.subtitle",
              "Track registered products, returns, and warranty status."
            )
      }
      actions={
        <PortalButton variant="ghost" startIcon="shopping_bag" onClick={() => navigate("/catalog")}>
          {t("common.browseCatalog", "Browse catalog")}
        </PortalButton>
      }
    >
      {query.error ? (
        <PortalAlert severity="error">
          {query.error?.response?.data?.error?.message ||
            query.error?.message ||
            t("registeredUnits.loadError", "Failed to load registered items.")}
        </PortalAlert>
      ) : null}
      {!distributor && (!canSubmitCore || !canSubmitWarranty) ? (
        <PortalAlert severity="info">
          {t("portal.viewOnlyNotice", "Some actions are hidden because your membership is view-only.")}
        </PortalAlert>
      ) : null}

      {query.isLoading ? (
        <PortalLoading label={t("registeredUnits.loading", "Loading registered items...")} />
      ) : !rows.length ? (
        <PortalAlert severity="info">
          {distributor
            ? t("registeredUnits.distributorEmpty", "No distributor-assigned items were found yet.")
            : t("registeredUnits.empty", "No registered or directly sold items were found yet.")}
        </PortalAlert>
      ) : (
        <Grid container spacing={2}>
          {rows.map((row) => {
            const lifecycle = row?.lifecycle || {};
            const id = row?.documentId;
            const activeClaimDocumentId = String(
              lifecycle?.activeWarranty?.documentId || ""
            ).trim();
            return (
              <Grid item xs={12} md={6} key={String(id)}>
                <PortalCard
                  accentTop
                  title={
                    row?.partNumber ||
                    row?.displayTag ||
                    t("registeredUnits.fallbackName", "Registered Item")
                  }
                  subtitle={
                    row?.tag
                      ? `Tag ${row.tag}`
                      : row?.serialNumber || "Serial pending"
                  }
                  action={<PortalStatusChip label={prettyStatus(lifecycle?.progressStatus)} tone="accent" />}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Box>
                      <MDTypography
                        variant="caption"
                        color="text"
                        sx={{ opacity: 0.7 }}
                      >
                        Registered
                      </MDTypography>
                      <MDTypography variant="button" fontWeight={700}>
                        {lifecycle?.registered ? "Yes" : "No"}
                      </MDTypography>
                    </Box>
                    <Box>
                      <MDTypography
                        variant="caption"
                        color="text"
                        sx={{ opacity: 0.7 }}
                      >
                        Core due
                      </MDTypography>
                      <MDTypography variant="button" fontWeight={700}>
                        {lifecycle?.coreDue ? "Yes" : "No"}
                      </MDTypography>
                    </Box>
                    <Box>
                      <MDTypography
                        variant="caption"
                        color="text"
                        sx={{ opacity: 0.7 }}
                      >
                        Active warranty
                      </MDTypography>
                      <MDTypography variant="button" fontWeight={700}>
                        {lifecycle?.activeWarranty?.status
                          ? prettyStatus(lifecycle.activeWarranty.status)
                          : "None"}
                      </MDTypography>
                    </Box>
                    <Box>
                      <MDTypography
                        variant="caption"
                        color="text"
                        sx={{ opacity: 0.7 }}
                      >
                        Disposition
                      </MDTypography>
                      <MDTypography variant="button" fontWeight={700}>
                        {prettyStatus(lifecycle?.disposition || "active")}
                      </MDTypography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <PortalButton variant="primary" onClick={() => navigate(`/account/valve-bodies/${id}`)}>
                      {t("portal.open", "Open")}
                    </PortalButton>
                    {!distributor && lifecycle?.coreDue && canSubmitCore ? (
                      <PortalButton
                        variant="ghost"
                        onClick={() => navigate(`/account/valve-bodies/${id}/core-return`)}
                      >
                        {t("portal.returnCore", "Return core")}
                      </PortalButton>
                    ) : null}
                    {!distributor && canSubmitWarranty ? (
                      <PortalButton
                        variant="ghost"
                        onClick={() => navigate(`/account/valve-bodies/${id}/warranty`)}
                      >
                        {t("warranty.title", "Warranty")}
                      </PortalButton>
                    ) : null}
                    {!distributor && activeClaimDocumentId ? (
                      <PortalButton
                        variant="ghost"
                        onClick={() =>
                          navigate(`/account/warranty-claims/${activeClaimDocumentId}`)
                        }
                      >
                        {t("portal.activeRma", "Active RMA")}
                      </PortalButton>
                    ) : null}
                  </Box>
                </PortalCard>
              </Grid>
            );
          })}
        </Grid>
      )}
    </CustomerPortalLayout>
  );
}
